import { customerTypes, genericPropertyDetailsKeys, propertyTypes } from "../../../../../@types/sharedSchema";
import type { CrmStatus, ProcessedListingAddress } from "../../../../../@types/sharedSchema";
import type { CrmContactRow } from "../../../../../@types/webotServer";

/**
 * The pure half of in-cell editing: what a cell may edit, what a control is seeded with,
 * and what the resulting write is allowed to contain.
 *
 * Split from the components for the same reason `crmQuickEditModel.ts` was: every trap in
 * this feature is a DATA question, not a rendering one. The row is a lossy projection of
 * the document being written — three of its fields do not mean what their names suggest,
 * and two of them are Cloud-Function-derived and unwritable — so the answers belong in one
 * file next to the measurements that produced them, not scattered across eleven controls.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * THE ROW COLUMN IS NOT THE DOCUMENT FIELD. This table is the whole feature.
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 *   dimension        row column(s)              document field(s) actually written
 *   ───────────────  ─────────────────────────  ───────────────────────────────────────
 *   name             name/firstName/lastName    callerFirstName + callerLastName
 *   budget           budget (budgetMin)         customerBudget  (+customerBudgetMin)
 *   budgetRent       budgetRent                 customerBudgetRent
 *   rooms            roomsMin/roomsMax          roomsMin + roomsMax
 *   crmStatus        crmStatus/crmSubStatus     crmStatus + crmSubStatus
 *   customerTypes    customerTypes              customerTypes
 *   cities           cities                     interestedInLocations   ⚠ whole array
 *   neighborhoods    neighborhoods              interestedInLocations   ⚠ whole array
 *   propertyTypes    propertyTypes              interestedInPropertyTypes
 *   tags             tags                       tags
 *   contactOrigin    contactOrigin              contactOrigin
 *   additionalNotes  additionalNotes            additionalNotes
 *   ── and the eight that need the DOCUMENT (see CRM_DOC_DIMENSIONS) ──────────────
 *   mail             —                          mail
 *   address          —                          address
 *   tehudatZehut     —                          tehudatZehut
 *   floors           —                          floorsMin + floorsMax
 *   size             —                          squareMeterMin + squareMeterMax
 *   amenities        —                          the 13 genericPropertyDetailsKeys
 *   budgetRange      —                          customerBudget + customerBudgetMin
 *   budgetRentRange  —                          customerBudgetRent + customerBudgetRentMin
 *
 * The em-dash in the middle column is the point of the second block: those eight have NO
 * row column, which is why they are gated on `subject.doc` and why a host without a
 * document must not offer them.
 *
 * Four of the first twelve rows are a rename, and writing the row's own column name instead
 * would be REJECTED by the server's whitelist (`contactWriteRules.ts`) with a 400 naming
 * the field — loudly, which is the good case. The two that would fail SILENTLY are the
 * ones this file works hardest to prevent: `name` (whose real field is `contactFullName`,
 * derived by `addContactRealtorAndOrgFields` and explicitly non-writable) and the two
 * location columns (`interestedInCitiesDistinct` / `…NeighborhoodsDistinct`, derived by
 * `flattenInterestedInLocations`). Writing any of those three races the Cloud Function
 * that owns them.
 */

// ───────────────────────────────────────────────────────────────────────────────
// The contract's vocabulary
// ───────────────────────────────────────────────────────────────────────────────

/**
 * The dimensions the TABLE offers — every one of them expressible from a mirror row.
 *
 * ⚠️ NOT the full set any more: {@link CRM_EDITABLE_DIMENSIONS} is this list PLUS
 * {@link CRM_DOC_DIMENSIONS}, and the split is what stops the table advertising a control
 * it cannot seed. A host holding only a row iterates THIS constant; a host holding the
 * document may offer either.
 *
 * Spelled with the ROW's column names (`cities`, not `city`) — unlike
 * `QUICK_EDIT_DIMENSIONS`, which used the server's emptiness-filter names. That is a
 * deliberate change of vocabulary, not an inconsistency: the quick editor's set was
 * chosen to mirror `contactEmptiness.ts` because its job was filling gaps the "פרטים
 * חסרים" queue routes to, so matching that file's names made the two comparable. This
 * set is chosen to mirror the TABLE's columns, because its job is "click the cell you
 * can see and edit what it shows" — so a reader holding `crmTablePlan.ts` in the other
 * hand is comparing like with like, and `cities` and `neighborhoods` are two columns
 * even though they are one document field.
 */
export const CRM_ROW_DIMENSIONS = [
  "name",
  "budget",
  "budgetRent",
  "rooms",
  "crmStatus",
  "customerTypes",
  "cities",
  "neighborhoods",
  "propertyTypes",
  "tags",
  "contactOrigin",
  "additionalNotes"
] as const;

/**
 * Dimensions that need the CONTACT DOCUMENT, and therefore only exist where a host has
 * one — today the סקירה tab's two panels, which render from a `RealEstateContact`.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * THE MIRROR ROW CANNOT EXPRESS THESE, AND THAT IS THE WHOLE REASON FOR THE SPLIT
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * `CrmContactRow` is the server's WIRE type for the Postgres mirror — 27 flat columns,
 * and `CrmContactUpdateResponse.contact` is literally one of them. Adding `mail`,
 * `floorsMin` or the amenity bag to it would be a lie about what that endpoint returns,
 * and making them real would mean a STORED generated column on `crm_contact`, which
 * rewrites all 5.1M rows under ACCESS EXCLUSIVE. So they are not row columns and never
 * will be; they are read from the document the host already holds.
 *
 * ⚠️ `budgetRange` IS NOT `budget`, AND MERGING THEM WOULD BE A DATA BUG.
 *
 * They look like the same field and they are two different QUANTITIES. The row's
 * `budgetMin` is the mirror's `budget_lo`, whose DDL is
 * `COALESCE(customerBudgetMin, customerBudget)` — so a contact with a single budget and
 * no minimum reads back as a 2M–2M range it never entered (see {@link budgetSeed}). The
 * DOCUMENT's `customerBudgetMin` is the raw stored minimum, which is null for exactly
 * that contact. One dimension serving both sources would mean one control that persists
 * an invented minimum on one host and not on the other; two names cost two switch
 * branches. The same applies to `budgetRentRange`.
 *
 * The rent/sale choice is the HOST's, made from the contact's customer types — never
 * re-inferred here from which value happens to be filled.
 */
export const CRM_DOC_DIMENSIONS = [
  "mail",
  "address",
  "tehudatZehut",
  "floors",
  // Built area (שטח בנוי). The mirror HAS `square_meter_min/max` columns but the wire row does
  // not carry them, so — like floors — it is read from the document the host holds.
  "size",
  "amenities",
  "budgetRange",
  "budgetRentRange"
] as const;

export const CRM_EDITABLE_DIMENSIONS = [...CRM_ROW_DIMENSIONS, ...CRM_DOC_DIMENSIONS] as const;

export type CrmEditableDimension = typeof CRM_EDITABLE_DIMENSIONS[number];
export type CrmDocDimension = typeof CRM_DOC_DIMENSIONS[number];

const EDITABLE_SET: ReadonlySet<string> = new Set(CRM_EDITABLE_DIMENSIONS);
const DOC_SET: ReadonlySet<string> = new Set(CRM_DOC_DIMENSIONS);

/**
 * Does this dimension need `subject.doc`?
 *
 * Consulted by {@link buildCellPatch} itself, so the rule is enforced at the point of the
 * write rather than trusted to every caller — a host that offers a document dimension
 * without a document produces NO KEY rather than a patch built against invented nulls.
 */
export const dimensionNeedsDocument = (dimension: CrmEditableDimension): dimension is CrmDocDimension =>
  DOC_SET.has(dimension);

/**
 * Narrows a raw string — a column id off the table plan, a URL fragment — to a dimension
 * this editor can serve.
 *
 * Exported because the CALLER decides which cells are editable, and it must be able to
 * ask rather than keep a second copy of the list. A second copy is how a column ships
 * with an `edit` marker for a dimension that has no control, which renders as a cell that
 * highlights on hover, accepts a click, and then does nothing.
 */
export function isCrmEditableDimension(value: string): value is CrmEditableDimension {
  return EDITABLE_SET.has(value);
}

/** The two dimensions that are two columns over ONE document field. */
export const isLocationDimension = (
  dimension: CrmEditableDimension
): dimension is "cities" | "neighborhoods" => dimension === "cities" || dimension === "neighborhoods";

// ───────────────────────────────────────────────────────────────────────────────
// The subject — what an editor is editing
// ───────────────────────────────────────────────────────────────────────────────

/**
 * The slice of the contact DOCUMENT that {@link CRM_DOC_DIMENSIONS} read and write.
 *
 * A projection rather than the whole `RealEstateContact`, for one reason: it is the
 * complete, enumerable list of what a document-backed editor may touch. A reviewer asking
 * "can this popup write X?" reads this type, not a 200-field interface.
 *
 * ⚠️ `undefined` AND `null` MEAN DIFFERENT THINGS, and the difference is load-bearing.
 *
 *   - a field that is `null`      → THE CONTACT HAS NO VALUE. Editing it is a real edit.
 *   - the whole `doc` `undefined` → THIS SOURCE CANNOT EXPRESS THESE FIELDS AT ALL.
 *
 * The second is the mirror-row host, and it is why every doc branch in
 * {@link buildCellPatch} bails on a missing `doc` instead of comparing against a default.
 * Defaulting an absent document to nulls would turn a popup opened over a source that
 * simply does not carry the field into a popup that CLEARS it — the same class of failure
 * the areas baseline guard exists to prevent, and the reason that guard is a status and
 * not an empty array.
 */
export type ContactEditableDoc = {
  mail: string | null;
  address: string | null;
  tehudatZehut: string | null;
  floorsMin: number | null;
  floorsMax: number | null;
  /** Built area the contact asked for, m². A stored 0 reads as unset (`isSetNumber`). */
  squareMeterMin: number | null;
  squareMeterMax: number | null;
  /** The 13 `genericPropertyDetailsKeys`, each read as a strict boolean. */
  amenities: Readonly<Record<string, boolean>>;
  /** RAW, never the mirror's COALESCE column — see {@link CRM_DOC_DIMENSIONS}. */
  customerBudget: number | null;
  customerBudgetMin: number | null;
  customerBudgetRent: number | null;
  customerBudgetRentMin: number | null;
};

/**
 * What the editor edits: always a row, optionally the document behind it.
 *
 * `CrmContactRow` is assignable to this, so every existing caller — the whole table — is
 * untouched and keeps its exact types. A host that HAS the document attaches it and
 * unlocks {@link CRM_DOC_DIMENSIONS}; a host that does not simply never offers them.
 */
export type CrmCellSubject = CrmContactRow & {
  readonly doc?: ContactEditableDoc;
};

/**
 * The amenity bag, read strictly.
 *
 * `=== true` rather than truthiness: these are stored as plain optional booleans and the
 * server validates them with `asBoolean`, which throws on anything else. A legacy
 * document carrying the string `"true"` must read as UNSET here rather than being echoed
 * back in a shape the write path would reject.
 */
export const readAmenities = (source: Record<string, unknown>): Record<string, boolean> => {
  const bag: Record<string, boolean> = {};
  genericPropertyDetailsKeys.forEach((key) => {
    bag[key] = source[key] === true;
  });
  return bag;
};

// ───────────────────────────────────────────────────────────────────────────────
// Money
// ───────────────────────────────────────────────────────────────────────────────

/**
 * Digits, grouped — `2500000` → `2,500,000`, as typed.
 *
 * The round trip is the whole requirement ("budget input (separators)"), and it is one
 * direction each: this function is the ONLY thing that puts separators in, and
 * {@link parseAmount} is the only thing that takes them out. Everything between them —
 * the draft, the change comparison, the optimistic patch — holds the formatted STRING,
 * so there is exactly one parse per save and no way for a half-parsed value to reach the
 * wire.
 *
 * `en-US` grouping rather than `he-IL` deliberately: both group by thousands with a
 * comma, but the digits are typed into a `dir="ltr"` island (see the control) and the
 * Latin locale is what that island's bidi context is built around.
 */
export const formatAmountInput = (raw: string): string => {
  const digits = (raw ?? "").replace(/[^\d]/g, "");
  return digits ? Number(digits).toLocaleString("en-US") : "";
};

/** The inverse. Empty string means "clear the field", which is a real value, hence null. */
export const parseAmount = (text: string): number | null => {
  const digits = (text ?? "").replace(/[^\d]/g, "");
  if (!digits) return null;
  const parsed = Number(digits);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * EMPTY MEANS JS-FALSY, INCLUDING A LITERAL 0 — the one-sentence rule both server engines
 * implement (`crm/contactEmptiness.ts` and the SQL generated columns beside it).
 *
 * Not theoretical, and the reason this is a helper rather than a `!= null` test: measured
 * on the 47,726-row mirror, `budget_hi IS NULL` is 45,367 rows while
 * `COALESCE(budget_hi, 0) = 0` is 45,540 — **173 contacts carry a literal 0 budget**, plus
 * 26 zero floors and 1 zero room count. A budget of 0 is not a budget.
 */
const isSetNumber = (value: number | null | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value) && value !== 0;

/**
 * ⚠️ FLOORS ARE NOT MONEY, AND 0 IS A REAL FLOOR. Do not unify this with
 * {@link isSetNumber} — the two differ on exactly one value and they differ deliberately.
 *
 * `isSetNumber` excludes 0 because a budget of 0 is not a budget, and the measurement in
 * its own comment records the other side of the same corpus scan: **26 contacts carry a
 * literal 0 floor**. 0 is קרקע and −1 is מרתף, both of them things a realtor picked on
 * purpose. Reusing the money test here would read a stored ground floor as UNSET, and
 * then — because an unset draft compares unequal to a set stored value — write
 * `floorsMin: null` over it on the first click-away that touched nothing.
 *
 * The panel's own chip already gets this right by testing null/undefined explicitly
 * rather than truthiness; this is the same rule, in the one place the write is decided.
 */
const isSetFloor = (value: number | null | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value);

/**
 * The single number to put in the budget field.
 *
 * ⚠️ WHY THIS IS NOT A RANGE, AND MUST NOT BECOME ONE.
 *
 * `row.budgetMin` is the mirror's `budget_lo` column, and its DDL is
 * `COALESCE(customerBudgetMin, customerBudget)`. So a contact carrying a single
 * `customerBudget: 2000000` and no minimum at all comes back as
 * `{ budget: 2000000, budgetMin: 2000000 }` — which a min/max control would render as a
 * "2M – 2M" range the realtor never entered, and saving it would PERSIST a
 * `customerBudgetMin` invented out of a storage detail.
 *
 * And the same contact does not read the same way twice: `contactRowProjection.ts` (which
 * replaces the row after a panel edit) projects the RAW `customerBudgetMin`, so the
 * identical contact flips to `budgetMin: null` the moment anything else is edited. A range
 * control would visibly change shape for no reason the realtor did anything to cause.
 *
 * So the cell is ONE field for ONE number, and it writes `customerBudget` alone.
 */
export const budgetSeed = (row: CrmContactRow): number | null => {
  if (isSetNumber(row.budget)) return row.budget;
  if (isSetNumber(row.budgetMin)) return row.budgetMin;
  return null;
};

/**
 * A minimum that is PROVABLY real — i.e. one that cannot be the COALESCE artefact above.
 *
 * `budget_lo !== budget_hi` is only possible when `customerBudgetMin` genuinely holds a
 * different number, so this is the one inference about the low bound that is safe from the
 * row alone. Shown as a caption under the field; never seeded into it.
 */
export const distinctStoredBudgetFloor = (row: CrmContactRow): number | null => {
  if (!isSetNumber(row.budgetMin) || !isSetNumber(row.budget)) return null;
  return row.budgetMin === row.budget ? null : row.budgetMin;
};

// ───────────────────────────────────────────────────────────────────────────────
// Name
// ───────────────────────────────────────────────────────────────────────────────

const collapseSpaces = (value: string): string => value.replace(/\s+/g, " ").trim();

/**
 * What to put in the name input.
 *
 * Built from `firstName` + `lastName` — the two fields that are actually WRITTEN — rather
 * than from `row.name`, which is `contactFullName`'s projection and is deduped
 * (`distinct([first, last]).join(" ")`, `contactRowProjection.ts:58`). For the contact
 * whose first and last names are the same word, that dedupe collapses "כהן כהן" to "כהן",
 * and seeding from it would silently delete a surname on the next save.
 *
 * `row.name` survives only as a fallback, for a mirror row that somehow carries the joined
 * name without its parts.
 */
export const nameSeed = (row: CrmContactRow): string => {
  const joined = collapseSpaces([row.firstName ?? "", row.lastName ?? ""].join(" "));
  return joined || collapseSpaces(row.name ?? "");
};

/**
 * One typed string back into the two fields the server accepts.
 *
 * First token is the given name, EVERYTHING ELSE is the surname — so "משה בן דוד" splits
 * to ("משה", "בן דוד") and round-trips through {@link nameSeed} byte-identically. The
 * naive `split(" ")` with a two-element destructure would drop "דוד" on the floor.
 */
export const splitName = (text: string): { first: string | null; last: string | null } => {
  const clean = collapseSpaces(text);
  if (!clean) return { first: null, last: null };
  const gap = clean.indexOf(" ");
  if (gap < 0) return { first: clean, last: null };
  return { first: clean.slice(0, gap), last: clean.slice(gap + 1) || null };
};

// ───────────────────────────────────────────────────────────────────────────────
// Enum vocabularies
// ───────────────────────────────────────────────────────────────────────────────

/**
 * The options a multi-select offers: the canonical enum, PLUS whatever this contact
 * already carries that is not in it.
 *
 * The union is the point, and it is a deliberate improvement on the quick editor's
 * behaviour rather than a copy of it. Legacy imports carry values outside
 * `customerTypes` / `propertyTypes`; the server validates both with `asEnumArray`, which
 * REFUSES THE WHOLE SAVE when it sees one. The quick editor carried such a value through
 * unfiltered (correctly — dropping it on seed would delete, silently, a value the realtor
 * never chose to remove) but rendered only the seven known toggles, so the offending value
 * was invisible: the realtor got a 400 they could neither understand nor clear.
 *
 * Rendering it as a selected, deselectable option keeps the "loud refusal beats quiet
 * deletion" property AND makes the refusal fixable in one click.
 */
export const optionsWithUnknown = (canonical: readonly string[], current: readonly string[]): string[] => {
  const extra = current.filter((value) => value && !canonical.includes(value));
  return [...canonical, ...new Set(extra)];
};

export const customerTypeOptions = (row: CrmContactRow): string[] =>
  optionsWithUnknown(customerTypes, row.customerTypes ?? []);

export const propertyTypeOptions = (row: CrmContactRow): string[] =>
  optionsWithUnknown(propertyTypes, row.propertyTypes ?? []);

/**
 * `crmStatus` has no equivalent helper, deliberately. It is a CLOSED enum written only by
 * this app, so unlike the two above there is no legacy-import tail to preserve — the
 * status control renders `crmStatuses` directly, and a value outside it is a bug to see
 * rather than an option to offer.
 *
 * `contactOrigin` has none either, and NOT for that reason — it has the widest legacy tail
 * of any field here. Its option list lives in `utils/contacts/contactOrigin.ts`
 * (`buildContactOriginOptions`) because the create/edit-contact modal renders the same
 * list, and a second copy of it in this file is exactly how the two would come to offer
 * different lead sources. It also needs no `row`: unlike the two arrays above, the
 * folded-in value is the single string the control already holds.
 */

// ───────────────────────────────────────────────────────────────────────────────
// The draft
// ───────────────────────────────────────────────────────────────────────────────

/**
 * One flat draft covering every dimension, rather than a discriminated union per control.
 *
 * A union would be more precise and is the obvious modelling choice, and it was rejected
 * for a concrete reason: the editor is mounted for exactly ONE dimension at a time and
 * unmounted on `onDone`, so only one group of these fields is ever live. A union would buy
 * type-safety over a variable that cannot vary, at the cost of a cast at every read in
 * `buildCellPatch`'s switch — where the switch has already proven which dimension it is.
 */
export type CrmCellDraft = {
  /** name · contactOrigin · additionalNotes · mail · address · tehudatZehut */
  text: string;
  /**
   * budget · budgetRent · the TOP of budgetRange / budgetRentRange / size — digits and
   * separators AS TYPED. Parsed once, at patch time.
   */
  amount: string;
  /** The BOTTOM of budgetRange / budgetRentRange / size. Same string discipline as `amount`. */
  amountMin: string;
  roomsMin: number | null;
  roomsMax: number | null;
  floorsMin: number | null;
  floorsMax: number | null;
  /**
   * The full 13-key amenity bag, every key present as a strict boolean.
   *
   * Complete rather than sparse because an UN-tick has to be expressible: the server
   * validates these with `asBoolean` (null throws) and omitting a key leaves the stored
   * `true` in place, so "the checkbox emptied and nothing saved" is the failure a partial
   * bag produces. {@link buildCellPatch} then emits only the keys that actually moved.
   */
  amenities: Record<string, boolean>;
  status: CrmStatus | null;
  subStatus: string | null;
  /** customerTypes · propertyTypes · tags */
  values: string[];
  /**
   * cities · neighborhoods.
   *
   * Held as state and handed straight back to `LocationPicker` as `pickedLocations`. That
   * picker mirrors the prop into its own state on EVERY change of it with no identity
   * guard (`useEffect(() => setLocations(pickedLocations), [pickedLocations])`), so a
   * fresh array literal each render is an infinite loop. It must be a referentially
   * stable value, which is why it lives in the draft rather than being derived.
   */
  locations: ProcessedListingAddress[];
};

/**
 * The draft as the contact currently stands.
 *
 * `locations` starts EMPTY and is filled only once the document hydrate lands — see
 * {@link buildCellPatch}, where the absence of that hydrate is what makes the areas key
 * unsendable. Seeding it from the row is impossible in any case: the row carries
 * flattened, unpaired `cities`/`neighborhoods` strings with the canonical ids stripped.
 */
export const seedCellDraft = (subject: CrmCellSubject, dimension: CrmEditableDimension): CrmCellDraft => {
  const row = subject;
  const doc = subject.doc;

  /** The top of whichever money control this is, as a formatted string. */
  const amountSeed = (): number | null => {
    if (dimension === "budgetRent") return isSetNumber(row.budgetRent) ? row.budgetRent : null;
    // RAW document values for the ranges — never `budgetSeed`, which reads the mirror's
    // COALESCE column and would materialise a minimum the contact does not carry.
    if (dimension === "budgetRange") return doc ? doc.customerBudget : null;
    if (dimension === "budgetRentRange") return doc ? doc.customerBudgetRent : null;
    if (dimension === "size") return doc && isSetNumber(doc.squareMeterMax) ? doc.squareMeterMax : null;
    if (dimension === "budget") return budgetSeed(row);
    return null;
  };

  const amountMinSeed = (): number | null => {
    if (dimension === "budgetRange") return doc ? doc.customerBudgetMin : null;
    if (dimension === "budgetRentRange") return doc ? doc.customerBudgetRentMin : null;
    if (dimension === "size") return doc && isSetNumber(doc.squareMeterMin) ? doc.squareMeterMin : null;
    return null;
  };

  return {
    text:
      dimension === "name"
        ? nameSeed(row)
        : dimension === "contactOrigin"
        ? row.contactOrigin ?? ""
        : dimension === "additionalNotes"
        ? row.additionalNotes ?? ""
        : dimension === "mail"
        ? doc?.mail ?? ""
        : dimension === "address"
        ? doc?.address ?? ""
        : dimension === "tehudatZehut"
        ? doc?.tehudatZehut ?? ""
        : "",
    amount: formatAmountInput(String(amountSeed() ?? "")),
    amountMin: formatAmountInput(String(amountMinSeed() ?? "")),
    roomsMin: isSetNumber(row.roomsMin) ? row.roomsMin : null,
    roomsMax: isSetNumber(row.roomsMax) ? row.roomsMax : null,
    // `isSetFloor`, not `isSetNumber` — see the two helpers, which differ on 0 alone.
    floorsMin: doc && isSetFloor(doc.floorsMin) ? doc.floorsMin : null,
    floorsMax: doc && isSetFloor(doc.floorsMax) ? doc.floorsMax : null,
    // A COPY. The control mutates the draft's bag through `patchDraft`, and the document
    // projection behind `doc.amenities` is rebuilt on every snapshot — sharing the object
    // would let a live refresh silently discard ticks made since the editor opened.
    amenities: doc ? { ...doc.amenities } : readAmenities({}),
    status: (row.crmStatus ?? null) as CrmStatus | null,
    subStatus: row.crmSubStatus ?? null,
    values:
      dimension === "customerTypes"
        ? [...(row.customerTypes ?? [])]
        : dimension === "propertyTypes"
        ? [...(row.propertyTypes ?? [])]
        : dimension === "tags"
        ? [...(row.tags ?? [])]
        : [],
    locations: []
  };
};

// ───────────────────────────────────────────────────────────────────────────────
// Locations: identity, and the one comparison that decides whether we may write
// ───────────────────────────────────────────────────────────────────────────────

/**
 * Everything about a picked area that the write actually carries.
 *
 * The canonical ids are IN the key on purpose: two entries can name the same city in the
 * same words and differ only in `canonicalLocationId`, and that id is exactly what lets a
 * saved area match listings written under a different source spelling. Comparing on the
 * display text alone would call such a pair equal, decide "unchanged", and silently drop
 * the id the picker had just resolved.
 */
const locationKey = (location: ProcessedListingAddress | null | undefined): string =>
  [
    location?.city ?? "",
    location?.neighborhood ?? "",
    location?.street ?? "",
    location?.canonicalLocationId ?? "",
    location?.canonicalLevel ?? ""
  ].join(" ");

/**
 * ORDER-INSENSITIVE set equality.
 *
 * `interestedInLocations` is a set of areas, not a sequence — nothing reads its order —
 * and the picker rebuilds the array on every internal edit, so an order-sensitive
 * comparison would report "changed" for a re-render that changed nothing and trigger a
 * whole-array write for free.
 */
export const sameLocationSets = (
  a: readonly ProcessedListingAddress[],
  b: readonly ProcessedListingAddress[]
): boolean => {
  if (a.length !== b.length) return false;
  const left = a.map(locationKey).sort();
  const right = b.map(locationKey).sort();
  return left.every((value, index) => value === right[index]);
};

const sameStringSets = (a: readonly string[], b: readonly string[]): boolean => {
  if (a.length !== b.length) return false;
  const left = [...a].sort();
  const right = [...b].sort();
  return left.every((value, index) => value === right[index]);
};

const distinct = (values: (string | null | undefined)[]): string[] => [
  ...new Set(values.map((value) => value?.trim()).filter((value): value is string => Boolean(value)))
];

const emptyToNull = (value: string): string | null => {
  const trimmed = collapseSpaces(value);
  return trimmed ? trimmed : null;
};

// ───────────────────────────────────────────────────────────────────────────────
// The patch
// ───────────────────────────────────────────────────────────────────────────────

export type CrmCellPatch = {
  /** The `fields` body for `POST /api/v1/contacts/update`. Empty means "nothing to save". */
  fields: Record<string, unknown>;
  /** The optimistic row overlay for the same change. Mirrors `contactRowProjection`. */
  rowPatch: Partial<CrmContactRow>;
};

/**
 * A min/max pair, put back in order — SWAPPED, never clamped.
 *
 * ⚠️ THE CONTROL ALSO DOES THIS, ON BLUR, AND BOTH ARE NEEDED. `CellMoneyRange` normalizes
 * when focus leaves a box so the realtor SEES the correction; this is the same rule at the
 * point the write is actually decided, and it is the one that survives the exits that
 * never blur — Enter, and the unmount flush. Without it an inverted pair typed and
 * committed with the keyboard reaches the document as a range no filter can match.
 *
 * A swap rather than a clamp because both numbers the realtor typed are real values they
 * chose; only the order is wrong. Clamping would silently discard one of them.
 */
const orderedBounds = (min: number | null, max: number | null): [number | null, number | null] =>
  min !== null && max !== null && min > max ? [max, min] : [min, max];

export type LocationsBaseline =
  /** The hydrate has not landed. The areas key is UNSENDABLE — see below. */
  | { status: "pending" }
  /** The hydrate landed; this is what the document held when it did. */
  | { status: "ready"; locations: ProcessedListingAddress[] };

/**
 * What to send, given one dimension's draft and where it started.
 *
 * EVERY branch is guarded by a value-level comparison against the row, and an unchanged
 * draft produces NO KEY AT ALL. That is not an optimisation: six Cloud Functions fire on
 * a contact write, and `updateContactFields` writes back unconditionally with no equality
 * bail, so a no-op save is six triggers and a re-render for nothing. The blur-driven
 * interaction model here makes it the COMMON case — a realtor who clicks a cell, reads the
 * value and clicks away has changed nothing, and does that far more often than they type.
 *
 * ⚠️ THE AREAS RULE IS THE WHOLE REASON THIS FUNCTION TAKES A BASELINE.
 *
 * `interestedInLocations` is a WHOLE-ARRAY write: the server replaces the stored array with
 * whatever arrives, and the row carries only flattened, unpaired `cities`/`neighborhoods`
 * strings with the canonical ids stripped — so the array cannot be reconstructed from the
 * row at all. If the editor dismissed while the document hydrate was still in flight and
 * sent `interestedInLocations: []` (the draft's initial value), it would DELETE every area
 * the contact had, along with every `canonicalLocationId` / `canonicalLevel` on them, and
 * the realtor's own listing alerts would stop firing with nothing on screen to say so.
 *
 * Two independent guards, and both are required:
 *
 *  1. `baseline.status !== 'ready'` — no hydrate, no key. This is the one that covers the
 *     save-on-dismiss race, which is the failure that actually happens: the interaction
 *     model here is "click away and it saves".
 *  2. `sameLocationSets(...)` — a hydrate that landed but was never edited also sends
 *     nothing. `LocationPicker` fires `onLocationsChange` ONCE ON MOUNT with whatever it
 *     was seeded with, so "the change handler ran" is not evidence the user touched
 *     anything; only a value-level difference from the baseline is.
 */
export const buildCellPatch = (
  subject: CrmCellSubject,
  dimension: CrmEditableDimension,
  draft: CrmCellDraft,
  baseline: LocationsBaseline
): CrmCellPatch => {
  const row = subject;
  const doc = subject.doc;
  const fields: Record<string, unknown> = {};
  const rowPatch: Partial<CrmContactRow> = {};

  /**
   * ⚠️ EVERY DOCUMENT BRANCH BELOW BAILS WITHOUT A DOCUMENT, and none of them may
   * "helpfully" fall back to a default.
   *
   * A missing `doc` does not mean "the contact has nothing" — it means THIS SOURCE
   * CANNOT SEE THE FIELD (see {@link ContactEditableDoc}). Comparing a draft against an
   * invented null would make every one of these a CLEAR of a value the editor was never
   * shown. Structurally the same failure the areas baseline guard exists to prevent, so
   * it gets the same shape: no source, no key.
   */
  const noDoc = dimensionNeedsDocument(dimension) && doc === undefined;

  switch (dimension) {
    // ── name ────────────────────────────────────────────────────────────────
    // The PAIR is written whenever either half moved, because both halves come from one
    // typed string: sending only the changed one would leave the document holding a
    // surname the realtor had just deleted. `contactFullName` is NOT sent — it is stamped
    // by `addContactRealtorAndOrgFields`, and the server's whitelist rejects it by name.
    case "name": {
      const next = splitName(draft.text);
      const currentFirst = emptyToNull(row.firstName ?? "");
      const currentLast = emptyToNull(row.lastName ?? "");
      if (next.first !== currentFirst || next.last !== currentLast) {
        fields.callerFirstName = next.first;
        fields.callerLastName = next.last;
        rowPatch.firstName = next.first;
        rowPatch.lastName = next.last;
        // DERIVE, never read back — same rule, and the same reasoning, as
        // `contactRowProjection.ts:58`. The overlay exists precisely for the seconds in
        // which the Cloud Function has not yet stamped the joined name.
        rowPatch.name = distinct([next.first, next.last]).join(" ") || null;
      }
      break;
    }

    // ── budget ──────────────────────────────────────────────────────────────
    case "budget": {
      const next = parseAmount(draft.amount);
      if (next !== budgetSeed(row)) {
        fields.customerBudget = next;
        rowPatch.budget = next;

        // Auto-correction, borrowed verbatim from `ScaleRangeStepper`'s rule ("lowering
        // max below min pulls min down to meet it") so the two controls in this app that
        // own a range behave the same way. Without it, typing a budget below a real stored
        // minimum leaves an INVERTED range on the document — 1.5M–1.2M — which no UI in
        // the app renders sensibly and which the overlap-semantics budget filter would
        // then never match. Only ever fires when the floor is PROVABLY real, so it can
        // never touch the COALESCE artefact.
        const floor = distinctStoredBudgetFloor(row);
        if (floor !== null && next !== null && next < floor) {
          fields.customerBudgetMin = next;
          rowPatch.budgetMin = next;
        }
      }
      break;
    }

    // ── rent budget ─────────────────────────────────────────────────────────
    // No COALESCE trap here and no floor to correct against: the row carries `budgetRent`
    // (`budget_rent_hi`) and no rent MINIMUM column at all, so there is nothing this cell
    // could infer about `customerBudgetRentMin` and nothing it can invert. It writes the
    // one number it was shown.
    case "budgetRent": {
      const next = parseAmount(draft.amount);
      const current = isSetNumber(row.budgetRent) ? row.budgetRent : null;
      if (next !== current) {
        fields.customerBudgetRent = next;
        rowPatch.budgetRent = next;
      }
      break;
    }

    // ── rooms ───────────────────────────────────────────────────────────────
    // Written as a PAIR whenever either bound moved. `ScaleRangeStepper` corrects the
    // other bound to keep min <= max, so a change to one is routinely a change to both;
    // sending only the edited half would persist the inversion the control just fixed on
    // screen. The server drops whichever half is genuinely unchanged.
    case "rooms": {
      const currentMin = isSetNumber(row.roomsMin) ? row.roomsMin : null;
      const currentMax = isSetNumber(row.roomsMax) ? row.roomsMax : null;
      if (draft.roomsMin !== currentMin || draft.roomsMax !== currentMax) {
        fields.roomsMin = draft.roomsMin;
        fields.roomsMax = draft.roomsMax;
        rowPatch.roomsMin = draft.roomsMin;
        rowPatch.roomsMax = draft.roomsMax;
      }
      break;
    }

    // ── status ──────────────────────────────────────────────────────────────
    // The sub-status is meaningful only under a status, so clearing the status clears it
    // too — otherwise a contact reads as "no status · פגישת הערכת שווי", which the pill
    // cannot render and the board cannot place.
    case "crmStatus": {
      const nextStatus = draft.status;
      const nextSub = nextStatus ? draft.subStatus : null;
      const currentStatus = (row.crmStatus ?? null) as CrmStatus | null;
      const currentSub = row.crmSubStatus ?? null;
      if (nextStatus !== currentStatus || nextSub !== currentSub) {
        fields.crmStatus = nextStatus;
        fields.crmSubStatus = nextSub;
        rowPatch.crmStatus = nextStatus;
        rowPatch.crmSubStatus = nextSub;
      }
      break;
    }

    // ── customer types ──────────────────────────────────────────────────────
    // ONLY the canonical array. The legacy `customerType` scalar is derived server-side by
    // `updateContactOfUser`, and the write whitelist REJECTS it outright — sending the
    // pair would 400 the entire save.
    case "customerTypes": {
      if (!sameStringSets(draft.values, row.customerTypes ?? [])) {
        fields.customerTypes = [...draft.values];
        rowPatch.customerTypes = [...draft.values];
      }
      break;
    }

    // ── property types ──────────────────────────────────────────────────────
    case "propertyTypes": {
      if (!sameStringSets(draft.values, row.propertyTypes ?? [])) {
        fields.interestedInPropertyTypes = [...draft.values];
        rowPatch.propertyTypes = [...draft.values];
      }
      break;
    }

    // ── tags ────────────────────────────────────────────────────────────────
    case "tags": {
      if (!sameStringSets(draft.values, row.tags ?? [])) {
        fields.tags = [...draft.values];
        rowPatch.tags = [...draft.values];
      }
      break;
    }

    // ── free text ───────────────────────────────────────────────────────────
    case "contactOrigin": {
      const next = emptyToNull(draft.text);
      if (next !== emptyToNull(row.contactOrigin ?? "")) {
        fields.contactOrigin = next;
        rowPatch.contactOrigin = next;
      }
      break;
    }

    case "additionalNotes": {
      // NOT collapsed through `emptyToNull` — a note is prose and its line breaks are
      // content. Only the outer whitespace goes, which is what the server's
      // `asTrimmedString` does to it anyway.
      const next = draft.text.trim() || null;
      const current = (row.additionalNotes ?? "").trim() || null;
      if (next !== current) {
        fields.additionalNotes = next;
        rowPatch.additionalNotes = next;
      }
      break;
    }

    // ── interested areas (two columns, ONE field) ───────────────────────────
    case "cities":
    case "neighborhoods": {
      if (baseline.status === "ready" && !sameLocationSets(draft.locations, baseline.locations)) {
        fields.interestedInLocations = draft.locations;
        // DERIVE, never read back. `interestedInCitiesDistinct` /
        // `interestedInNeighborhoodsDistinct` are stamped by the
        // `flattenInterestedInLocations` Cloud Function, so they are stale for exactly the
        // seconds this overlay exists to cover.
        //
        // BOTH columns are patched whichever one was clicked, because both are projections
        // of the single array that just changed: editing from the `cities` cell can
        // perfectly well add a neighbourhood, and patching only the clicked column would
        // leave the sibling cell two pixels away showing the pre-edit value until a
        // refetch.
        rowPatch.cities = distinct(draft.locations.map((location) => location?.city));
        rowPatch.neighborhoods = distinct(draft.locations.map((location) => location?.neighborhood));
      }
      break;
    }

    // ── document-only: three plain scalars ──────────────────────────────────
    //
    // No `rowPatch` on any of these, and that is correct rather than an omission: none of
    // the three is a `CrmContactRow` column, so there is no list row to patch. The hosts
    // that offer them render from a LIVE Firestore document, which repaints on its own —
    // the optimistic overlay exists for the mirror's drain lag, and the mirror does not
    // carry these fields at all.
    //
    // ⚠️ NEITHER `mail` NOR `tehudatZehut` IS SHAPE-VALIDATED HERE, deliberately, and the
    // server does not validate them either — its own comment says refusing a save over a
    // neighbouring legacy value is what made the old form's save button "randomly not
    // clickable". The editor refuses a malformed value at the CONTROL, before it ever
    // reaches this function; what lands here is either valid or the realtor's second,
    // deliberate dismissal, which discards instead of saving.
    case "mail": {
      if (noDoc) break;
      const next = emptyToNull(draft.text);
      if (next !== emptyToNull(doc!.mail ?? "")) fields.mail = next;
      break;
    }

    case "address": {
      if (noDoc) break;
      const next = emptyToNull(draft.text);
      if (next !== emptyToNull(doc!.address ?? "")) fields.address = next;
      break;
    }

    case "tehudatZehut": {
      if (noDoc) break;
      const next = emptyToNull(draft.text);
      if (next !== emptyToNull(doc!.tehudatZehut ?? "")) fields.tehudatZehut = next;
      break;
    }

    // ── floors ──────────────────────────────────────────────────────────────
    // Written as a PAIR whenever either bound moved, for the same reason rooms is: the
    // stepper corrects the other bound to keep min <= max, so a change to one is routinely
    // a change to both, and sending only the edited half would persist the inversion the
    // control just fixed on screen.
    case "floors": {
      if (noDoc) break;
      const currentMin = isSetFloor(doc!.floorsMin) ? doc!.floorsMin : null;
      const currentMax = isSetFloor(doc!.floorsMax) ? doc!.floorsMax : null;
      if (draft.floorsMin !== currentMin || draft.floorsMax !== currentMax) {
        fields.floorsMin = draft.floorsMin;
        fields.floorsMax = draft.floorsMax;
      }
      break;
    }

    // ── built area ──────────────────────────────────────────────────────────
    //
    // Two free-typed numbers, so the bounds are compared and emitted INDEPENDENTLY like the
    // budget range (a typed box moves one bound, not both) and re-ordered here as well as in
    // the control, for the exits that never blur.
    //
    // ⚠️ `isSetNumber`, NOT `isSetFloor`: unlike a floor, 0 is not a size. A stored 0 (the
    // dialog's number box can leave one) reads as unset, so an untouched popup over such a
    // contact compares equal and writes nothing — and a typed 0 CLEARS the bound rather than
    // storing a minimum the matcher would have to special-case.
    case "size": {
      if (noDoc) break;
      const asSize = (text: string): number | null => {
        const parsed = parseAmount(text);
        return isSetNumber(parsed) ? parsed : null;
      };
      const [nextMin, nextMax] = orderedBounds(asSize(draft.amountMin), asSize(draft.amount));
      const currentMin = isSetNumber(doc!.squareMeterMin) ? doc!.squareMeterMin : null;
      const currentMax = isSetNumber(doc!.squareMeterMax) ? doc!.squareMeterMax : null;
      if (nextMin !== currentMin) fields.squareMeterMin = nextMin;
      if (nextMax !== currentMax) fields.squareMeterMax = nextMax;
      break;
    }

    // ── amenities ───────────────────────────────────────────────────────────
    //
    // ONLY THE KEYS THAT MOVED, each as a literal boolean. Three rules in one branch:
    //
    //  - a key that did not change is ABSENT, because six Cloud Functions fire on a
    //    contact write and a 13-key patch for one tick is twelve fields of noise;
    //  - an UN-tick sends `false`, never `null` (the server's `asBoolean` throws on null
    //    and 400s the whole patch) and never an omission (which would leave the stored
    //    `true` in place — a checkbox that visibly empties and saves nothing);
    //  - the iteration is over the CANONICAL key list, not over the draft's own keys, so
    //    a bag that somehow lost a key cannot silently stop writing that amenity.
    case "amenities": {
      if (noDoc) break;
      genericPropertyDetailsKeys.forEach((key) => {
        const next = draft.amenities[key] === true;
        const current = doc!.amenities[key] === true;
        if (next !== current) fields[key] = next;
      });
      break;
    }

    // ── budget as a RANGE (document hosts only) ─────────────────────────────
    //
    // Both bounds are compared and emitted INDEPENDENTLY: the control keeps them ordered,
    // so an edit to the top can move the bottom, and either may be cleared on its own.
    // Reads the RAW `customerBudgetMin`, never `row.budgetMin` — see the ⚠️ on
    // {@link CRM_DOC_DIMENSIONS} for why those are two different quantities.
    case "budgetRange": {
      if (noDoc) break;
      const [nextMin, nextMax] = orderedBounds(parseAmount(draft.amountMin), parseAmount(draft.amount));
      if (nextMax !== (doc!.customerBudget ?? null)) fields.customerBudget = nextMax;
      if (nextMin !== (doc!.customerBudgetMin ?? null)) fields.customerBudgetMin = nextMin;
      // The list row CAN express the top of a sale budget, so it is patched — the mirror
      // is a drain tick behind and the row may be on screen beside this panel.
      if ("customerBudget" in fields) rowPatch.budget = nextMax;
      break;
    }

    case "budgetRentRange": {
      if (noDoc) break;
      const [nextMin, nextMax] = orderedBounds(parseAmount(draft.amountMin), parseAmount(draft.amount));
      if (nextMax !== (doc!.customerBudgetRent ?? null)) fields.customerBudgetRent = nextMax;
      if (nextMin !== (doc!.customerBudgetRentMin ?? null)) fields.customerBudgetRentMin = nextMin;
      if ("customerBudgetRent" in fields) rowPatch.budgetRent = nextMax;
      break;
    }

    default: {
      // Exhaustiveness: a dimension added to the list without a branch here is a compile
      // error, not a control that silently saves nothing.
      const never: never = dimension;
      return never;
    }
  }

  return { fields, rowPatch };
};

/** Is there anything to save? Cheaper to ask than to rebuild the patch at every render. */
export const cellPatchIsEmpty = (patch: CrmCellPatch): boolean => Object.keys(patch.fields).length === 0;
